import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TenantService } from './tenant.service';
import { environment } from '../../environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { Timesheet } from '../models/timesheet';
import { AuxiliarService } from './auxiliar.service';
import { TimesheetDrawerComponent } from '../components/drawers/timesheet-drawer/timesheet-drawer.component';
import { TranslateService } from '@ngx-translate/core';
import { TimesheetRecord } from '../models/timesheet-record';
import { Employee } from '../models/employee';
import { Doc } from '../models/document';
import { TimesheetManualDrawerComponent } from '../components/drawers/timesheet-manual-drawer/timesheet-manual-drawer.component';
import { AuthService } from './auth.service';
import { Absence } from '../models/absence';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  private lastTimesheetSubject = new BehaviorSubject<void>(undefined);
  public lastTimesheetObservable = this.lastTimesheetSubject.asObservable();
  private timesheetsSubject = new BehaviorSubject({} as Timesheet);
  public timesheetsObservable = this.timesheetsSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private tenantService: TenantService,
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
    private authService: AuthService,
  ) { }

  // trigger header employee last timesheet update
  public updateUserLastTimesheet(): void {
    this.lastTimesheetSubject.next();
  }

  // trigger timesheets update
  public updateUserTimesheets(timesheet: Timesheet): void {
    this.timesheetsSubject.next(timesheet);
  }

  // [GET] last timesheet record
  public getLastTimesheetRecord(): Observable<{ [index: string]: any; }> {
    const tenant = this.authService.getTokenFieldValue('defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', 'last'),
        { observe: 'response', headers }
      );
  }

  // [GET] my timesheets
  public getMyTimesheets(date: string): Observable<{ [index: string]: any; }> {
    const tenant = this.authService.getTokenFieldValue('defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${_.replace(environment.api_endpoints.timesheets, ':id', 'me')}/${moment(date).format('YYYYMMDD')}`,
        { observe: 'response', headers }
      );
  }

  // [GET] employee timesheets
  public getEmployeeTimesheets(date: string, employee: Employee): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const defaultTenant = _.get(employee, 'defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': defaultTenant || tenant.id});
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${_.replace(environment.api_endpoints.timesheets, ':id', employee.id)}/${moment(date).format('YYYYMMDD')}`,
        { observe: 'response', headers }
      );
  }

  // [POST] create timesheet record
  public createTimesheetRecord(timesheetRecord: TimesheetRecord): Observable<{ [index: string]: any; }> {
    const tenant = this.authService.getTokenFieldValue('defaultTenant');
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', 'me'),
        timesheetRecord, { observe: 'response', headers }
      );
  }

  // [POST] create timesheet record manually
  public createTimesheetRecordManual(timesheetRecord: TimesheetRecord, employee?: Employee): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', `manual/${employee?.id || 'me'}`),
        timesheetRecord, { observe: 'response', headers }
      );
  }

  // [GET] documents
  public getDocs(): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.auths}/my-documents`,
        { observe: 'response', headers }
      );
  }

  // [GET] download doc
  public downloadDoc(doc: Doc): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(environment.api_server + `${environment.api_endpoints.auths}/my-document/${doc.id}`,
      { observe: 'response', headers, responseType: 'blob' });
  }

  // [PATCH] confirm timesheet record
  public confirmTimesheetRecord(idRecord: string, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.patch<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', `confirm/${idRecord}`),
        {}, { observe: 'response', headers }
      );
  }

  // [PATCH] reject timesheet record
  public rejectTimesheetRecord(idRecord: string, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.patch<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', `refuse/${idRecord}`),
        {}, { observe: 'response', headers }
      );
  }

  // [DELETE] delete timesheet record
  public deleteTimesheetRecord(idRecord: string, employee: Employee | undefined): Observable<{ [index: string]: any; }> {
    const tenant = _.get(employee, 'defaultTenant', this.authService.getTokenFieldValue('defaultTenant'));
    const headers = new HttpHeaders({ 'X-Tenant': tenant });
    return this.httpClient.delete<HttpResponse<object>>
      (
        environment.api_server + _.replace(environment.api_endpoints.timesheets, ':id', idRecord),
        { observe: 'response', headers }
      );
  }

  // open timesheet detail
  public openTimesheetDetail(timesheet: Timesheet, timesheetsMonth: Timesheet[], employee: Employee | undefined, absences: Absence[]): void {
    const timesheetsMonthHours = timesheetsMonth.length ? _.sumBy(timesheetsMonth, 'timeworked') : 0;
    this.auxiliarService.openDrawer(TimesheetDrawerComponent, { 
      absences,
      employee,
      timesheet,
      timesheetsMonthHours,
      position: 'right',
      iconRight: 'pi pi-clock',
      titleRight: this.translate.instant('title.timesheet', {date: moment(timesheet.date).format('DD/MM/YYYY')}),
      width: '600px'
    });
  }

  // open new manual timesheet
  public async openManualTimesheetForm(employee?: Employee): Promise<boolean> {
    const res = await this.auxiliarService.openDrawer(TimesheetManualDrawerComponent, { 
      employee,
      position: 'right',
      iconRight: 'pi pi-clock',
      titleRight: this.translate.instant('title.new-timesheet'),
      width: '600px'
    });
    return res;
  }

  // get timesheet record icon
  public getTimesheetRecordIcon(record: TimesheetRecord): string {
    switch(record.type) {
      case 'break':
        return 'fa fa-coffee';
      case 'work':
        return 'pi pi-hammer';
      default:
        return 'pi pi-clock'
    }
  }
}
